import { round2 } from './money';

/**
 * Trexa's commission on a vendor's earnings — the percentage deducted from every payout.
 *
 * It is CONFIGURED PER VENDOR in Admin › Vendors › <vendor> › "Trexa fee" (`Vendor.feeRate`), on
 * top of the platform default in Admin › Finance (`PlatformSetting.vendorFeePct`). This file used
 * to export a hard-coded `PLATFORM_FEE_RATE = 0.10` instead, so a vendor put on 0% (or 15%) was
 * still shown — and paid — 10% on every screen: the admin console was writing a number nothing
 * read. Resolve the rate through `vendorFeePctFor` on EVERY surface that quotes or moves a payout.
 */
export const DEFAULT_VENDOR_FEE_PCT = 10;

/** Where a vendor's fee % can come from, most specific first. */
export interface VendorFeePctSources {
  /** The rate frozen onto this booking for this vendor when the invoice went out (`BookingFeeRate`). */
  snapshot?: { feeRate?: number | null } | null;
  /** The vendor's currently configured rate (`Vendor.feeRate`, Admin › Vendors). */
  vendor?: { feeRate?: number | null } | null;
  /** Today's platform-wide default (`PlatformSetting.vendorFeePct`, Admin › Finance). */
  setting?: { vendorFeePct?: number | null } | null;
}

/** A configured percentage, or null when the source has nothing to say. 0 IS a rate (fee waived) — a
 *  `||` chain reads it as "unset" and silently restores the default the admin just cleared. */
function pct(v?: number | null): number | null {
  if (v === undefined || v === null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? Math.max(0, n) : null;
}

/**
 * The fee % that applies to ONE vendor on ONE booking.
 *
 * The booking's own snapshot wins: changing a vendor's rate in the admin console must not re-price
 * a booking already invoiced ("Bookings already invoiced keep the rate they were invoiced at" — the
 * admin fee card says so). Then the vendor's configured rate, then the platform default.
 *
 * Pass only what a surface has: a catalogue screen quoting "your take-home" has no booking, so it
 * passes `vendor` + `setting` and gets today's rate.
 */
export function vendorFeePctFor(src: VendorFeePctSources): number {
  return pct(src.snapshot?.feeRate)
    ?? pct(src.vendor?.feeRate)
    ?? pct(src.setting?.vendorFeePct)
    ?? DEFAULT_VENDOR_FEE_PCT;
}

/** The commission itself, in cents-accurate ringgit. A 0% vendor is charged nothing. */
export function vendorFeeOn(amount: number, feePct: number): number {
  if (!(feePct > 0)) return 0;
  return round2(Math.max(0, amount || 0) * feePct / 100);
}

/**
 * Customer protection fee — the percentage Trexa charges the CUSTOMER, configured in
 * Admin › Finance (`PlatformSetting.customerProtectionPct`, mockup `platformPolicy.custProtPct`).
 *
 * It is charged ON TOP of the order: on an RM 20,450 booking at 1% she pays RM 20,654.50, while
 * the vendor is still owed RM 20,450. Escrow only ever holds the ORDER total — the fee is Trexa's
 * revenue and never enters a vendor payout, an escrow balance, or the balance-due maths.
 *
 * Every surface that quotes what the customer pays must go through these two helpers, or the
 * pay-card, the invoice document and the ledger drift apart (mockup `bkProtPct` / `bkProtFee`).
 */

/** Shape of what these helpers need — keeps them free of SDK poco imports. */
export interface ProtectionPctSources {
  /** The rate snapshotted onto the booking when its invoice was issued (`Booking.protectionPct`). */
  booking?: { protectionPct?: number | null } | null;
  /** Today's platform-wide rate (`PlatformSetting.customerProtectionPct`). */
  setting?: { customerProtectionPct?: number | null } | null;
}

/**
 * The protection % that applies to ONE booking. The booking's own snapshot wins: changing the
 * rate in the admin console must never re-price a booking the customer has already been quoted.
 * Only when that snapshot is missing (bookings created before the rate was snapshotted) does
 * today's platform rate apply. 0 — or no setting row at all — means the fee is off.
 */
export function protectionPctFor(src: ProtectionPctSources): number {
  const snap = src.booking?.protectionPct;
  if (snap !== undefined && snap !== null) return Math.max(0, Number(snap) || 0);
  return Math.max(0, Number(src.setting?.customerProtectionPct ?? 0) || 0);
}

/**
 * The fee itself, computed off the LIVE order total (mockup `bkProtFee`). Deliberately derived
 * rather than read back from `Booking.protectionFee`: a stored amount goes stale the moment a
 * service is removed or swapped, and the customer would be billed a fee for an order she no
 * longer has. Only the PCT is snapshotted; the ringgit figure always follows the current total.
 */
export function protectionFeeOn(total: number, pct: number): number {
  if (!(pct > 0)) return 0;
  return round2(Math.max(0, total || 0) * pct / 100);
}

/** "1" / "1.5" — a rate as it reads in a label, without a trailing ".00" (mockup `fmtPct`). Used for
 *  both fees: "Trexa fee (7.5%)" and "Trexa protection (1%)". */
export function fmtPct(rate: number): string {
  const n = Math.round((rate || 0) * 100) / 100;
  return n === Math.round(n) ? String(Math.round(n)) : String(n);
}

// ─────────────────────────────────────────────────────────────────────────────
// The protection fee AFTER it has been collected — what Trexa still holds of it
// ─────────────────────────────────────────────────────────────────────────────

/** The only two ledger columns these helpers read — keeps them free of SDK poco imports. */
export interface ProtectionFeeLedgerRow { entryType?: string | null; amount?: number | null }

/**
 * The protection fee Trexa STILL holds on a booking — the SIGNED sum of its `protection_fee`
 * rows, so a refund (written as a negative row of the same type) nets it back to zero.
 *
 * A refund is booked against the fee's own entry type rather than as a `refund` row on purpose:
 * every escrow figure on every surface excludes `protection_fee` (it is Trexa's revenue, never
 * custody), so a `refund` row would have shrunk the vendor's escrow instead of Trexa's fee
 * income, and Admin › Finance "Fees earned" — a signed sum of the same rows — would never have
 * moved. See [[customer-protection-fee]].
 */
export function protectionFeeHeld(ledger: ProtectionFeeLedgerRow[]): number {
  const net = (ledger ?? [])
    .filter(e => e.entryType === 'protection_fee')
    .reduce((s, e) => s + (Number(e.amount) || 0), 0);
  return Math.max(0, round2(net));
}

/** How much of the protection fee has already been given back (absolute value of the negative rows). */
export function protectionFeeReturned(ledger: ProtectionFeeLedgerRow[]): number {
  const back = (ledger ?? [])
    .filter(e => e.entryType === 'protection_fee' && (Number(e.amount) || 0) < 0)
    .reduce((s, e) => s + Math.abs(Number(e.amount) || 0), 0);
  return round2(back);
}
